import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useContent } from '../hooks/useContent';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  type?: 'website' | 'article' | 'business';
}

const SEOHead: React.FC<SEOHeadProps> = ({ 
  title = 'Kamana Yoga - Hatha-Vinyasa Flow Yoga in Kapfenberg & online',
  description = 'Kamana Yoga - Hatha-Vinyasa Flow Yoga in Kapfenberg & online. Für mehr Leichtigkeit & Freiheit im Leben. Yoga im Freien und flexibles Online-Yoga.',
  keywords = ['Yoga', 'Hatha-Vinyasa Flow', 'Yoga Kapfenberg', 'Online Yoga', 'Yoga im Freien', 'Kamana Yoga'],
  image = '/yoga/kamana-logo.png',
  type = 'website'
}) => {
  const location = useLocation();
  const { getPageContent } = useContent();
  
  const pageContent = getPageContent(location.pathname);
  const url = `https://kamana-yoga.at${location.pathname}`;
  
  // Extract structured data from page content
  const structuredData = pageContent ? generateStructuredData(pageContent) : null;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords.join(', '));
    
    // Open Graph Tags (for ChatGPT, Perplexity, etc.)
    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:image', image, 'property');
    updateMetaTag('og:url', url, 'property');
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:locale', 'de_AT', 'property');
    
    // Twitter Cards
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    
    // AI Search Optimization
    updateMetaTag('ai:searchable', 'true');
    updateMetaTag('ai:context', JSON.stringify(pageContent?.sections || []));
    
    // Canonical URL
    updateLinkTag('canonical', url);
    
    // Add structured data
    if (structuredData) {
      addStructuredData(structuredData);
    }
  }, [title, description, keywords, image, type, url, pageContent, structuredData]);

  return null;
};

function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  
  element.content = content;
}

function updateLinkTag(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  
  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }
  
  element.href = href;
}

function addStructuredData(data: any) {
  // Remove existing structured data
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) {
    existing.remove();
  }
  
  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

function generateStructuredData(pageContent: any) {
  const baseData = {
    '@context': 'https://schema.org',
    '@type': 'YogaStudio',
    name: pageContent.title || 'Kamana Yoga',
    description: pageContent.intro || pageContent.meta?.description || 'Hatha-Vinyasa Flow Yoga in Kapfenberg & online',
    url: `https://kamana-yoga.at${pageContent.path}`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Kapfenberg',
      addressRegion: 'Steiermark',
      addressCountry: 'AT'
    }
  };

  // Add LocalBusiness if contact info exists
  if (pageContent.content?.contact) {
    return {
      ...baseData,
      '@type': 'LocalBusiness',
      address: {
        '@type': 'PostalAddress',
        streetAddress: pageContent.content.contact.address,
        addressLocality: 'Marktschellenberg',
        addressRegion: 'Bayern',
        addressCountry: 'DE'
      },
      telephone: pageContent.content.contact.phone,
      email: pageContent.content.contact.email,
      priceRange: '$$',
      openingHours: pageContent.openingHours ? parseOpeningHours(pageContent.openingHours) : undefined
    };
  }

  return baseData;
}

function parseOpeningHours(openingHours: string): string[] {
  // Simple parser - could be improved
  const hours: string[] = [];
  
  if (openingHours.includes('täglich')) {
    hours.push('Mo-Su 00:00-23:59');
  } else if (openingHours.includes('Montag bis Sonntag')) {
    hours.push('Mo-Su 00:00-23:59');
  } else if (openingHours.includes('Montag bis Freitag')) {
    const times = openingHours.match(/(\d{1,2}):(\d{2})\s*bis\s*(\d{1,2}):(\d{2})/);
    if (times) {
      hours.push(`Mo-Fr ${times[1]}:${times[2]}-${times[3]}:${times[4]}`);
    }
  }
  
  return hours;
}

export default SEOHead;

